import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { apiFetch } from '@/utils/api';
import { ArrowLeft, Save } from 'lucide-react';
import { CurrentUserProvider } from '@/hooks/useCurrentUser';

interface Vendor {
    id: string;
    name: string;
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface PaymentInstrument {
    id: string;
    name: string;
    instrument_type: string;
}

interface PaymentFormData {
    cost_center_id: string;
    vendor_id: string;
    direction: string;
    amount: string;
    currency: string;
    payment_instrument_id: string;
    value_date: string;
    received_at: string;
    status: string;
    invoice_reference: string;
    external_id: string;
}

const initialFormData: PaymentFormData = {
    cost_center_id: '',
    vendor_id: '',
    direction: 'AP',
    amount: '',
    currency: 'HUF',
    payment_instrument_id: '',
    value_date: new Date().toISOString().split('T')[0],
    received_at: '',
    status: 'pending',
    invoice_reference: '',
    external_id: '',
};

const directionOptions = [
    { value: 'AR', label: 'Accounts Receivable (Money In)' },
    { value: 'AP', label: 'Accounts Payable (Money Out)' },
];

const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'posted', label: 'Posted' },
    { value: 'voided', label: 'Voided' },
    { value: 'reversed', label: 'Reversed' },
];

const currencyOptions = [
    { value: 'HUF', label: 'HUF (Hungarian Forint)' },
    { value: 'EUR', label: 'EUR (Euro)' },
    { value: 'USD', label: 'USD (US Dollar)' },
];

export default function NewPaymentPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const [formData, setFormData] = useState<PaymentFormData>(initialFormData);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [paymentInstruments, setPaymentInstruments] = useState<PaymentInstrument[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        fetchReferenceData();
    }, []);

    const fetchReferenceData = async () => {
        try {
            const [vendorsResponse, costCentersResponse] = await Promise.all([
                apiFetch('/api/vendors/'),
                apiFetch('/api/cost-centers/'),
            ]);

            if (vendorsResponse.ok) {
                const vendorsData = await vendorsResponse.json();
                setVendors(vendorsData.vendors || []);
            }

            if (costCentersResponse.ok) {
                const costCentersData = await costCentersResponse.json();
                setCostCenters(costCentersData.cost_centers || []);
            }

            // Try to fetch payment instruments, but don't fail if endpoint doesn't exist
            try {
                const instrumentsResponse = await apiFetch('/api/payment-instruments/');
                if (instrumentsResponse.ok) {
                    const instrumentsData = await instrumentsResponse.json();
                    setPaymentInstruments(instrumentsData.payment_instruments || []);
                }
            } catch (error) {
                console.warn('Payment instruments endpoint not available:', error);
                setPaymentInstruments([]);
            }
        } catch (error) {
            console.error('Error fetching reference data:', error);
        }
    };

    const handleInputChange = (field: keyof PaymentFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.cost_center_id) {
            newErrors.cost_center_id = 'Cost center is required';
        }
        if (!formData.direction) {
            newErrors.direction = 'Direction is required';
        }
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Amount must be greater than 0';
        }
        if (!formData.currency) {
            newErrors.currency = 'Currency is required';
        }
        if (!formData.value_date) {
            newErrors.value_date = 'Value date is required';
        }
        if (!formData.status) {
            newErrors.status = 'Status is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Prepare the data for submission
            const submitData = {
                ...formData,
                amount: parseFloat(formData.amount),
                // Only include optional fields if they have values
                ...(formData.vendor_id && { vendor_id: formData.vendor_id }),
                ...(formData.payment_instrument_id && { payment_instrument_id: formData.payment_instrument_id }),
                ...(formData.received_at && { received_at: new Date(formData.received_at).toISOString() }),
                ...(formData.invoice_reference && { invoice_reference: formData.invoice_reference }),
                ...(formData.external_id && { external_id: formData.external_id }),
                value_date: new Date(formData.value_date).toISOString(),
            };

            const response = await apiFetch('/api/payments/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submitData),
            });

            if (response.ok) {
                const data = await response.json();
                // Redirect to the created payment
                navigate({ to: `/payments/${data.id}`});
            } else {
                console.error('API response not ok:', response.status, response.statusText);
                alert('Failed to create payment. Please try again.');
            }
        } catch (error) {
            console.error('Error creating payment:', error);
            alert('Failed to create payment. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center space-x-4">
                        <Button variant="ghost" onClick={() => navigate({ to: '..' })}>
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <h1 className="text-3xl font-bold tracking-tight">Create New Payment</h1>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Basic Information */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Payment Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="direction">Direction *</Label>
                                        <Select value={formData.direction} onValueChange={(value) => handleInputChange('direction', value)}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select direction" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {directionOptions.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.direction && <p className="text-sm text-red-500">{errors.direction}</p>}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="amount">Amount *</Label>
                                            <Input
                                                id="amount"
                                                type="number"
                                                step="0.01"
                                                placeholder="0.00"
                                                value={formData.amount}
                                                onChange={(e) => handleInputChange('amount', e.target.value)}
                                            />
                                            {errors.amount && <p className="text-sm text-red-500">{errors.amount}</p>}
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="currency">Currency *</Label>
                                            <Select value={formData.currency} onValueChange={(value) => handleInputChange('currency', value)}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select currency" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {currencyOptions.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.currency && <p className="text-sm text-red-500">{errors.currency}</p>}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="status">Status *</Label>
                                        <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {statusOptions.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.status && <p className="text-sm text-red-500">{errors.status}</p>}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="value_date">Value Date *</Label>
                                            <Input
                                                id="value_date"
                                                type="date"
                                                value={formData.value_date}
                                                onChange={(e) => handleInputChange('value_date', e.target.value)}
                                            />
                                            {errors.value_date && <p className="text-sm text-red-500">{errors.value_date}</p>}
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="received_at">Received At</Label>
                                            <Input
                                                id="received_at"
                                                type="date"
                                                value={formData.received_at}
                                                onChange={(e) => handleInputChange('received_at', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Related Information */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Related Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="cost_center_id">Cost Center *</Label>
                                        <Select value={formData.cost_center_id} onValueChange={(value) => handleInputChange('cost_center_id', value)}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select cost center" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {costCenters.filter(cc => cc.is_active).map((costCenter) => (
                                                    <SelectItem key={costCenter.id} value={costCenter.id}>
                                                        {costCenter.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.cost_center_id && <p className="text-sm text-red-500">{errors.cost_center_id}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="vendor_id">Vendor</Label>
                                        <Select value={formData.vendor_id} onValueChange={(value) => handleInputChange('vendor_id', value)}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select vendor (optional)" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="">No vendor</SelectItem>
                                                {vendors.map((vendor) => (
                                                    <SelectItem key={vendor.id} value={vendor.id}>
                                                        {vendor.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {paymentInstruments.length > 0 && (
                                        <div className="space-y-2">
                                            <Label htmlFor="payment_instrument_id">Payment Method</Label>
                                            <Select value={formData.payment_instrument_id} onValueChange={(value) => handleInputChange('payment_instrument_id', value)}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select payment method (optional)" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="">No payment method</SelectItem>
                                                    {paymentInstruments.map((instrument) => (
                                                        <SelectItem key={instrument.id} value={instrument.id}>
                                                            {instrument.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="invoice_reference">Invoice Reference</Label>
                                        <Input
                                            id="invoice_reference"
                                            placeholder="Invoice number or reference"
                                            value={formData.invoice_reference}
                                            onChange={(e) => handleInputChange('invoice_reference', e.target.value)}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="external_id">External ID</Label>
                                        <Input
                                            id="external_id"
                                            placeholder="External system reference"
                                            value={formData.external_id}
                                            onChange={(e) => handleInputChange('external_id', e.target.value)}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end space-x-4 mt-6">
                            <Button type="button" variant="outline" onClick={() => navigate({ to: '..' })}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4 mr-2" />
                                        Create Payment
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}

export const Route = createFileRoute('/payments/new')({
  component: NewPaymentPage,
})
